/**
 * Utilitaires de détection et persistance de langue pour les widgets
 *
 * Gère la détection de la langue préférée de l'utilisateur selon une cascade
 * de priorités et la persistance de son choix dans localStorage.
 *
 * @module widgets/common/utils/languageDetection
 * @author Équipe Brydge
 * @since 1.0.0
 */

export const STORAGE_KEY = 'brydge_widget_language';
export const I18N_STORAGE_KEY = 'i18nextLng';

/**
 * Détecte la meilleure langue à utiliser selon la cascade de priorité :
 * 1. Paramètre URL (?language=en)
 * 2. LocalStorage (sélection précédente de l'utilisateur)
 * 3. Langue du navigateur (navigator.language)
 * 4. Langue par défaut de l'entreprise
 * 5. Fallback français
 *
 * @param {string} urlLang - Langue depuis paramètre URL
 * @param {string} companyDefault - Langue par défaut de l'entreprise
 * @param {string[]} enabledLanguages - Langues activées pour l'entreprise
 * @return {string} Code de langue sélectionné (fr, en, nl, de)
 *
 * @example
 * // Détection avec langue URL
 * detectLanguage('en', 'fr', ['fr', 'en', 'nl'])
 * // => 'en'
 *
 * @example
 * // Détection sans langue URL, fallback vers navigateur
 * detectLanguage(null, 'fr', ['fr', 'en', 'nl'])
 * // => 'en' (si navigator.language = 'en-US')
 */
export function detectLanguage(urlLang, companyDefault, enabledLanguages = ['fr']) {
  // 1. Vérifier paramètre URL
  if (urlLang && enabledLanguages.includes(urlLang)) {
    return urlLang;
  }

  // 2. Vérifier localStorage
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && enabledLanguages.includes(stored)) {
      return stored;
    }
  } catch {
    // localStorage indisponible (ex: navigation privée)
  }

  // 3. Vérifier langue du navigateur
  const browserLang = navigator.language?.split('-')[0]; // 'en-US' -> 'en'
  if (browserLang && enabledLanguages.includes(browserLang)) {
    return browserLang;
  }

  // 4. Utiliser langue par défaut de l'entreprise
  if (companyDefault && enabledLanguages.includes(companyDefault)) {
    return companyDefault;
  }

  // 5. Fallback français (ou première langue disponible)
  return enabledLanguages.includes('fr') ? 'fr' : enabledLanguages[0];
}

/**
 * Persiste la langue sélectionnée dans localStorage
 *
 * Sauvegarde le choix de langue de l'utilisateur pour les prochaines visites.
 * Gère gracieusement les erreurs si localStorage est indisponible.
 *
 * @param {string} language - Code de langue à persister (fr, en, nl, de)
 *
 * @example
 * persistLanguage('en')
 * // localStorage.getItem('brydge_widget_language') => 'en'
 */
export function persistLanguage(language) {
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // localStorage indisponible (ex: navigation privee)
  }
}

/**
 * Récupère la langue persistée dans localStorage (si disponible)
 *
 * @return {string|null} Code de langue ou null si non disponible
 *
 * @example
 * getPersistedLanguage()
 * // => 'en' (si précédemment sauvegardé)
 * // => null (si pas de langue sauvegardée ou localStorage inaccessible)
 */
export function getPersistedLanguage() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Supprime la langue persistée de localStorage
 *
 * Utile pour réinitialiser les préférences de l'utilisateur.
 *
 * @example
 * clearPersistedLanguage()
 * // Langue supprimée du localStorage
 */
export function clearPersistedLanguage() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // localStorage indisponible (ex: navigation privee)
  }
}

/**
 * Réinitialise toutes les préférences de langue stockées
 *
 * Utilisé quand le support multilingue est désactivé pour forcer
 * le retour à la langue par défaut de l'entreprise. Nettoie les clés
 * localStorage et force i18next à changer de langue.
 *
 * @param {string} defaultLanguage - Langue par défaut à appliquer (fr, en, nl, de)
 * @param {function} changeLanguage - Fonction i18n pour changer la langue
 *
 * @example
 * resetLanguageToDefault('fr', i18n.changeLanguage)
 * // Nettoie localStorage et force le retour au français
 */
export function resetLanguageToDefault(defaultLanguage, changeLanguage) {
  try {
    // Nettoyer les préférences de langue du widget
    localStorage.removeItem(STORAGE_KEY);
    // Nettoyer la clé i18next globale
    localStorage.removeItem(I18N_STORAGE_KEY);

    // Forcer le retour à la langue par défaut
    if (changeLanguage) {
      changeLanguage(defaultLanguage);
    }
  } catch {
    // localStorage indisponible (ex: navigation privée)
  }
}
